import React, { useState, useEffect, useCallback, useMemo } from "react";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Loading from "components/Loading";
import { useRouter } from "next/router";
import Share from "./Share";
import { apiDirectCall } from "customHooks/useApi/api";
import ModalAlert from "components/ModalAlert";
import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import toLower from "lodash/toLower";
import { useSession } from "next-auth/react";
import { getLabelFromFieldNames } from "../../helpers/helper";
import Image from "next/image";
import TermsAndPolicyContent from "components/TermsAndPolicy/Content";
import useCompleoCareerPageContext from "../../useCompleoCareerPageContext";

interface IJobDetailProps {
    name: string;
    componentMetadata: any;
    careerPageClasses: any;
    keyData: Compleo.IObject;
}

const JobDetail = ({
    name,
    componentMetadata,
    careerPageClasses,
    keyData,
}: IJobDetailProps) => {
    const [data] = useCompleoCareerPageContext();

    const {
        companyId,
        uniqueLink,
        moduleName,
        initialData,
        enableMultipleApplicationsPerJob,
        language,
        companyPrivacyPolicyUrl,
        isCustomDomain,
    } = data;

    const { t } = useTranslation(moduleName);

    const jobTitleStyle = componentMetadata.customStyle?.filter(
        (fc: any) => fc.name === "jobTitle"
    )[0];

    const jobSubtitleStyle = componentMetadata.customStyle?.filter(
        (fc: any) => fc.name === "jobSubtitle"
    )[0];

    const jobDescriptionStyle = componentMetadata.customStyle?.filter(
        (fc: any) => fc.name === "jobDescription"
    )[0];

    const applyButtonStyle = componentMetadata.customStyle?.filter(
        (fc: any) => fc.name === "applyButton"
    )[0];

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            jobTitle: {
                color:
                    jobTitleStyle?.fontColor ||
                    theme.palette?.primary?.main ||
                    "black",
                fontSize:
                    jobTitleStyle?.fontSize ||
                    theme.typography.h2.fontSize ||
                    "20px",
            },
            jobSubtitle: {
                color:
                    jobSubtitleStyle?.fontColor ||
                    theme.palette.text.primary ||
                    "black",
                fontSize:
                    jobSubtitleStyle?.fontSize || theme.typography.h4.fontSize,
            },
            jobDescription: {
                color:
                    jobDescriptionStyle?.fontColor ||
                    theme.palette.text.primary ||
                    "black",
                fontSize:
                    jobDescriptionStyle?.fontSize ||
                    theme.typography.body1.fontSize,
            },
            jobDetailContainer: {
                paddingTop: "15px",
            },
            actionsArea: {
                marginTop: "3rem",
                display: "block",
            },
            sticky: {
                position: "sticky",
                top: "9rem",
            },
            shareArea: {
                paddingTop: "15px",
            },
            applyButton: {
                color:
                    applyButtonStyle?.fontColor ||
                    theme.palette.getContrastText(theme.palette.primary.main),
                backgroundColor:
                    applyButtonStyle?.backgroundColor ||
                    theme.palette.primary.main,
                "&:hover": {
                    backgroundColor:
                        applyButtonStyle?.backgroundColorHover ||
                        theme.palette.secondary.main,
                    color:
                        applyButtonStyle?.fontColorHover ||
                        theme.palette.getContrastText(
                            theme.palette.secondary.main
                        ),
                },
            },
        })
    );
    const [fullHref, setFullHref] = useState("");
    const [isApplicationEnabled, setIsApplicationEnabled] = useState<
        boolean | null
    >(null);
    const [openApplicationDenied, setOpenApplicationDenied] = useState(false);
    const [openJobNotAvailableModal, setOpenJobNotAvailableModal] =
        useState(false);

    const classes = useStyles();
    const { jobData, companyData } = initialData || {};
    const [imageContainerHeight, setImageContainerHeight] = useState("0");
    const [imageContainerWidth, setImageContainerWidth] = useState("0");

    const { id } = keyData;
    const router = useRouter();
    const { data: session } = useSession();
    const user = session?.user?.email;

    const defaultContentFields = useMemo<Compleo.IObject>(
        () => ({
            title: [
                {
                    fieldName: "title",
                    order: 1,
                },
            ],
            subtitle: [
                {
                    fieldName: "location.country",
                    order: 1,
                },
                {
                    fieldName: "location.city|location.provinceOrState",
                    separator: "/",
                    order: 2,
                },
                {
                    fieldName: "workingModel",
                    order: 3,
                },
            ],
            description: [
                {
                    fieldName: "description",
                    order: 1,
                },
            ],
        }),
        []
    );

    const contentFields = useMemo<Compleo.IObject>(
        () => ({ ...componentMetadata.contentFields, ...defaultContentFields }),
        [componentMetadata, defaultContentFields]
    );

    useEffect(() => {
        const host = window.location.host;
        const baseUrl = `https://${host}`;

        setFullHref(`${baseUrl}${router.asPath}`);
    }, [router.pathname]);

    useEffect(() => {
        if (
            jobData &&
            jobData.isAvailableOnCareersSite !== undefined &&
            !jobData?.isAvailableOnCareersSite
        ) {
            handleClickOpenJobNotAvailableModal();
        }
    }, [jobData]);

    useEffect(() => {
        if (user && !enableMultipleApplicationsPerJob) {
            const getCareersApplicantData = async (
                companyId: number,
                email: string
            ) => {
                try {
                    const response = await apiDirectCall(
                        "/applicant/searchcareersapplicantdata",
                        "post",
                        {
                            companyId,
                            email,
                        },
                        undefined,
                        undefined,
                        "credentials"
                    );

                    const applicationEnabled =
                        !response.data ||
                        !response.data?.data?.jobs ||
                        response.data.data.jobs.filter(
                            (j: any) => j.jobId === id
                        ).length === 0;
                    setIsApplicationEnabled(applicationEnabled);
                } catch (err) {
                    setIsApplicationEnabled(true);
                }
            };

            getCareersApplicantData(companyId, user);
        } else {
            setIsApplicationEnabled(true);
        }
    }, [user, enableMultipleApplicationsPerJob, companyId, id]);

    const handleClickOpenApplicationDenied = useCallback(() => {
        setOpenApplicationDenied(true);
    }, []);

    const handleClickCloseApplicationDenied = useCallback(() => {
        setOpenApplicationDenied(false);
    }, []);

    const handleClickOpenJobNotAvailableModal = useCallback(() => {
        setOpenJobNotAvailableModal(true);
    }, []);

    const handleClickCloseJobNotAvailableModal = useCallback(() => {
        setOpenJobNotAvailableModal(false);
    }, []);

    if (!fullHref || isApplicationEnabled === null) {
        return <Loading />;
    }

    const applyButtonLabel = t(
        `${componentMetadata.componentName}_applyButtonText`
    ).includes("_applyButtonText")
        ? language === "pt-BR"
            ? "Candidatar-se"
            : "Apply"
        : t(`${componentMetadata.componentName}_applyButtonText`);

    const modalApplicationDeniedTitle = t(
        "ModalApplicationNotAllowed_Title"
    ).includes("ModalApplicationNotAllowed")
        ? language === "pt-BR"
            ? "Candidatura não permitida"
            : "Application not allowed"
        : t("ModalApplicationNotAllowed_Title");
    const modalApplicationDeniedText = t(
        "ModalApplicationNotAllowed_Text"
    ).includes("ModalApplicationNotAllowed")
        ? language === "pt-BR"
            ? "Você se candidatou anteriormente à essa vaga. Não é possível candidatar-se novamente."
            : "You've already applied for this job. You can't apply again."
        : t("ModalApplicationNotAllowed_Text");
    const modalCloseButtonText = t("ModalApplicationNotAllowed_Close").includes(
        "ModalApplicationNotAllowed"
    )
        ? language === "pt-BR"
            ? "Fechar"
            : "Close"
        : t("ModalApplicationNotAllowed_Close");

    return (
        <Container maxWidth="lg" className={careerPageClasses.container}>
            <section>
                {jobData &&
                    (jobData.isAvailableOnCareersSite === undefined ||
                        jobData.isAvailableOnCareersSite) && (
                        <Grid
                            container
                            spacing={4}
                            className={classes.jobDetailContainer}
                            direction={
                                componentMetadata.applyButtonType ===
                                "rightSticked"
                                    ? "row"
                                    : componentMetadata.applyButtonType ===
                                      "bottom"
                                    ? "column"
                                    : "row-reverse"
                            }
                        >
                            <Grid
                                item
                                xs={12}
                                sm={
                                    componentMetadata.applyButtonType ===
                                    "bottom"
                                        ? 12
                                        : 8
                                }
                                md={
                                    componentMetadata.applyButtonType ===
                                    "bottom"
                                        ? 12
                                        : 8
                                }
                            >
                                <Grid container direction="column">
                                    <Grid item>
                                        <Typography
                                            variant="h2"
                                            className={classes.jobTitle}
                                        >
                                            {getLabelFromFieldNames(
                                                contentFields.title,
                                                jobData,
                                                language
                                            ) ?? jobData.title}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            className={classes.jobSubtitle}
                                        >
                                            {getLabelFromFieldNames(
                                                contentFields.subtitle,
                                                jobData,
                                                language
                                            ) ??
                                                jobData.location?.country
                                                    ?.label ??
                                                jobData.workingModel?.label}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="body1"
                                            className={classes.jobDescription}
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    getLabelFromFieldNames(
                                                        contentFields.description,
                                                        jobData,
                                                        language
                                                    ) ?? jobData.description,
                                            }}
                                        ></Typography>
                                        {jobData.jobImage?.key && (
                                            <div
                                                style={{
                                                    position: "relative",
                                                    height: imageContainerHeight,
                                                    width: "100%",
                                                }}
                                            >
                                                <Image
                                                    src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/${jobData.jobImage.key}`}
                                                    layout="fill"
                                                    objectFit="contain"
                                                    alt={jobData.title}
                                                    onLoad={({ target }) => {
                                                        const {
                                                            naturalWidth,
                                                            naturalHeight,
                                                        } =
                                                            target as HTMLImageElement;

                                                        setImageContainerWidth(
                                                            `${naturalWidth}`
                                                        );
                                                        setImageContainerHeight(
                                                            `${naturalHeight}px`
                                                        );
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sm={
                                    componentMetadata.applyButtonType ===
                                    "bottom"
                                        ? 12
                                        : 4
                                }
                                md={
                                    componentMetadata.applyButtonType ===
                                    "bottom"
                                        ? 12
                                        : 4
                                }
                                className={classes.actionsArea}
                            >
                                <Grid
                                    container
                                    direction="column"
                                    className={classes.sticky}
                                    spacing={2}
                                >
                                    <Grid item>
                                        {isApplicationEnabled ? (
                                            <Link
                                                href={
                                                    isCustomDomain
                                                        ? `/jobapplication/${id}`
                                                        : `/${toLower(
                                                              uniqueLink
                                                          )}/jobapplication/${id}`
                                                }
                                                passHref
                                            >
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    className={
                                                        classes.applyButton
                                                    }
                                                >
                                                    {applyButtonLabel}
                                                </Button>
                                            </Link>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="large"
                                                className={classes.applyButton}
                                                onClick={
                                                    handleClickOpenApplicationDenied
                                                }
                                            >
                                                {applyButtonLabel}
                                            </Button>
                                        )}
                                    </Grid>
                                    <Grid item style={{ textAlign: "justify" }}>
                                        <TermsAndPolicyContent
                                            language={language}
                                            companyName={
                                                companyData.companyFancyName ??
                                                companyData.companyName
                                            }
                                            companyPrivacyPolicyUrl={
                                                companyPrivacyPolicyUrl
                                            }
                                            buttonText={applyButtonLabel}
                                        />
                                    </Grid>
                                    {componentMetadata.showShareButtons && (
                                        <Grid item>
                                            <Share
                                                language={language}
                                                t={t}
                                                options={[
                                                    {
                                                        type: "facebook",
                                                        url: fullHref,
                                                        data: {
                                                            quote: jobData.title,
                                                        },
                                                    },
                                                    {
                                                        type: "twitter",
                                                        url: fullHref,
                                                        data: {},
                                                    },
                                                    {
                                                        type: "whatsapp",
                                                        url: fullHref,
                                                        data: {},
                                                    },
                                                    {
                                                        type: "linkedin",
                                                        url: fullHref,
                                                        data: {
                                                            title: jobData.title,
                                                        },
                                                    },
                                                    {
                                                        type: "email",
                                                        url: fullHref,
                                                        data: {
                                                            subject: `Veja essa vaga: ${jobData.title}`,
                                                        },
                                                    },
                                                    {
                                                        type: "link",
                                                        url: fullHref,
                                                        data: {},
                                                    },
                                                ]}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                <ModalAlert
                    open={openApplicationDenied}
                    handleClose={handleClickCloseApplicationDenied}
                    title={modalApplicationDeniedTitle}
                    contentText={modalApplicationDeniedText}
                    closeButtonText={modalCloseButtonText}
                />
                <ModalAlert
                    open={openJobNotAvailableModal}
                    handleClose={handleClickCloseJobNotAvailableModal}
                    title={
                        t("ModalJobNotAvailable_Title").includes(
                            "ModalJobNotAvailable"
                        )
                            ? "Vaga indisponível"
                            : t("ModalJobNotAvailable_Title")
                    }
                    contentText={
                        t("ModalJobNotAvailable_Text").includes(
                            "ModalJobNotAvailable"
                        )
                            ? "Vaga não existente ou indisponível para novas candidaturas."
                            : t("ModalJobNotAvailable_Text")
                    }
                    closeButtonText="Ok"
                    redirectUrlOnClose={
                        isCustomDomain ? "/joblist" : `/${uniqueLink}/joblist`
                    }
                />
            </section>
        </Container>
    );
};

export default JobDetail;
